import React, { useContext, useEffect, useState } from 'react'
import BoxContext from '../BoxContext'
import { useForm } from 'antd/es/form/Form'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import localeData from 'dayjs/plugin/localeData'
import { Color, APPOINTMENT_COLORS } from '../../../Common/Color'
import OneLineAppointment from '../OneLineAppointment'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import BoxCalendarPageXL from './BoxCalendarPageXL'
import BoxCalendarPageXS from './BoxCalendarPageXS'
import { appointmentTitle } from '../../../Common/Common'
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons'
import { put } from '../../../Common/api'
import { Flex } from 'antd'
import { t } from 'i18next'

dayjs.extend(weekday)
dayjs.extend(localeData)

const transformAppointment = (appointment) => {
  return {
    id: appointment.id,
    type: 'appointment',
    start: dayjs(appointment.begin_date).format(),
    end: dayjs(appointment.end_date).subtract(1, 'second').format(),
    color: APPOINTMENT_COLORS[appointment.type],
    title: appointmentTitle(appointment),
    className: !appointment.sms_confirmed && 'appointment-stripped'
  }
}

const transformSlot = (slot, xl) => {
  return {
    id: slot.id,
    type: 'slot',
    color: slot.closed ? Color.closed_slot : Color.free_slot,
    title: slotTitle(slot.closed, xl),
    closed: slot.closed,
    start: dayjs(slot.start_date).format(),
    end: dayjs(slot.end_date).format()
  }
}

const slotTitle = (closed, xl) => {
  if (xl) {
    return closed ? t('Admin.BoxCalendar.slotTitle.xl.closed') : t('Admin.BoxCalendar.slotTitle.xl.free')
  } else {
    return closed ? t('Admin.BoxCalendar.slotTitle.xs.closed') : t('Admin.BoxCalendar.slotTitle.xs.free')
  }
}

const getMinMaxHour = (periods) => {
  let minHour = null
  let maxHour = null
  periods.forEach((period) => {
    if (minHour === null || dayjs(period.start).hour() < minHour) {
      minHour = dayjs(period.start).hour()
    }
    if (maxHour === null || dayjs(period.end).hour() > maxHour) {
      maxHour = dayjs(period.end).hour()
    }
  })
  minHour -= 2
  maxHour += 2
  return [minHour, maxHour]
}

const businessHours = (periods) => {
  const hours = []
  if (!periods) { return hours }
  periods.forEach((period) => (
    hours.push({
      daysOfWeek: [period.day.week_day],
      startTime: dayjs(period.start).format('HH:mm'),
      endTime: dayjs(period.end).format('HH:mm')
    })
  ))
  return hours
}

export default function BoxCalendarPage () {
  const { appointments, loadAppointments, box, saveAppointment, loadSlots, slots, periods, loading } = useContext(BoxContext)
  const [appointmentModalVisible, setAppointmentModalVisible] = useState(false)
  const [rangeDisable, setRangeDisable] = useState(false)
  const [events, setEvents] = useState([])
  const [appointmentForm] = useForm()
  const params = new URLSearchParams(location.search)
  const day = params.get('day')
  const screens = useBreakpoint()

  useEffect(() => {
    updateEvents(appointments, slots)
  }, [appointments, slots])

  const updateEvents = (appointments, slots) => {
    const formattedAppointments = appointments?.map((appointment) => transformAppointment(appointment))
    const formattedSlots = slots.filter(slot => !slot.appointment_id).map((slot) => transformSlot(slot, screens.xl))
    setEvents([...formattedSlots, ...formattedAppointments])
  }

  const getAppointment = (id) => (
    appointments.find((appointment) => appointment.id === id)
  )

  const updateSlot = async (id) => {
    await put({
      url: `admin/boxes/${box.id}/slots/${id}`,
      params: {},
      onSuccess: (json) => {
        const index = events.findIndex(e => e.id === id)
        const updatedEvents = [...events]
        updatedEvents[index] = transformSlot(json, screens.xl)
        setEvents(updatedEvents)
      }
    })
  }

  const slotSelected = (slot) => {
    const type = slot.extendedProps.closed ? 'MaintenanceAppointment' : 'PatientAppointment'
    appointmentForm.resetFields()
    appointmentForm.setFieldsValue({
      type,
      range: [dayjs(slot.start), dayjs(slot.start).add(box.appointment_duration, 'second')],
      is_now: false,
      appointment_date: dayjs(slot.start)
    })
    setRangeDisable(false)

    setAppointmentModalVisible(true)
  }

  const renderEventContent = (eventInfo) => {
    return (
      <div style={{ fontWeight: 'bold' }} >
        { eventInfo.event.extendedProps.type === 'slot' && screens.xl && renderSlotXl(eventInfo) }
        { eventInfo.event.extendedProps.type === 'slot' && !screens.xl && renderSlotXS(eventInfo) }
        { eventInfo.event.extendedProps.type === 'appointment' &&
          <OneLineAppointment appointment={getAppointment(eventInfo.event.id)} />
        }
      </div>
    )
  }

  const renderSlotXl = (eventInfo) => {
    const Icon = eventInfo.event.extendedProps.closed ? PlusSquareOutlined : MinusSquareOutlined
    return (
      <Flex justify='space-between' style={{ whiteSpace: 'nowrap', overflowX: 'clip', height: '100%' }}
        onClick={() => slotSelected(eventInfo.event)} >
        {eventInfo.event.title}
        <Icon style={{ color: 'white', fontSize: '3em' }} onClick={(event) => { event.stopPropagation(); updateSlot(eventInfo.event.id) }} />
      </Flex>
    )
  }

  const renderSlotXS = (eventInfo) => {
    return <div onClick={() => slotSelected(eventInfo.event)}>{ eventInfo.event.title }</div>
  }

  const selected = (info) => {
    appointmentForm.resetFields()
    appointmentForm.setFieldsValue({
      type: 'PatientAppointment',
      range: [dayjs(info.start).minute(box.slot_schedule), dayjs(info.start).add(box.appointment_duration, 'second').minute(box.slot_schedule)],
      is_now: false,
      appointment_date: dayjs(info.start).minute(box.slot_schedule)
    })
    setRangeDisable(false)

    setAppointmentModalVisible(true)
  }

  const onAdd = async (values) => {
    values = { ...values, phone: values.phone.slice(1) }
    await saveAppointment(values, appointmentForm, () => setAppointmentModalVisible(false))
  }

  const loadCalendarData = async (params) => {
    await loadAppointments(box.id, params.start)
    await loadSlots(params.start, params.end)
  }

  const checkBoxAvailablity = () => {
    const now = dayjs()
    const appointment = appointments.find((appointment) =>
      dayjs(appointment.begin_date).isBefore(now) && dayjs(appointment.end_date).isAfter(now)
    )
    const nextAppointment = appointments.find((appointment) =>
      dayjs(appointment.begin_date).isBefore(now.add(box.appointment_duration, 'second')) && dayjs(appointment.end_date).isAfter(now.add(box.appointment_duration, 'second  '))
    )
    return !(appointment || nextAppointment)
  }

  const boxAvailableNow = checkBoxAvailablity()
  const [minHour, maxHour] = getMinMaxHour(periods)

  const props = {
    box,
    appointmentModalVisible,
    loading,
    appointmentForm,
    setAppointmentModalVisible,
    onAdd,
    rangeDisable,
    boxAvailableNow,
    setRangeDisable,
    loadCalendarData,
    day,
    businessHours,
    events,
    renderEventContent,
    selected,
    minHour,
    maxHour
  }

  const displayContent = () => {
    if (screens.xl) {
      return <BoxCalendarPageXL {...props} />
    } else {
      return <BoxCalendarPageXS {...props} />
    }
  }

  return (
    <>
      { displayContent() }
    </>
  )
}
